import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import SiteHeader from "../components/SiteHeader.jsx";
import SiteFooter from "../components/SiteFooter.jsx";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import analytics, { planToSubscriptionType } from "../lib/analytics";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const formRef = useRef(null);
  const [payfast, setPayfast] = useState(null); // { url, fields } — signed server-side
  const viewTracked = useRef(false);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  // GA4 view_pricing — fire once when the Pricing page opens (ref guard also
  // prevents a duplicate under React StrictMode double-mount in dev).
  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    const sourcePage = document.referrer || "direct";
    analytics.viewPricing(sourcePage, user?.subscription_plan || user?.subscription_status || "none");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-submit the hidden form once Payfast fields are ready.
  useEffect(() => {
    if (payfast && formRef.current) {
      formRef.current.submit();
    }
  }, [payfast]);

  const subscribe = async (plan) => {
    if (!user) { nav("/signup"); return; }
    if (user && !user.hpcsa_number) { nav("/complete-profile"); return; }
    if (plan.price_zar === 0) {
      toast.info("You already have free access on your account.");
      return;
    }
    setLoading(plan.id);
    // Ask the backend to build a SIGNED Payfast request. The signature is
    // computed server-side with the secret passphrase (never exposed to the
    // browser), then we auto-submit the returned fields to Payfast.
    try {
      const { data } = await api.post("/subscription/initialize", {
        plan_id: plan.id,
        return_url: `${window.location.origin}/payment-success`,
        cancel_url: `${window.location.origin}/payment-cancelled`,
      });
      // GA4 begin_checkout — user confirmed and is being redirected into PayFast.
      analytics.beginCheckout(planToSubscriptionType(plan.id) || plan.interval, plan.price_zar);
      setPayfast({ url: data.payfast_url, fields: data.fields });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment — please try again.");
      setLoading(null);
    }
  };

  const startTrial = async () => {
    if (!user) { nav("/signup"); return; }
    if (user && !user.hpcsa_number) { nav("/complete-profile"); return; }
    setTrialLoading(true);
    try {
      await api.post("/subscription/start-trial");
      await refresh();
      toast.success("7-day Premium trial started");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start trial");
    } finally {
      setTrialLoading(false);
    }
  };

  const freePlan = plans.find((p) => p.id === "plan-free");
  const paid = plans.filter((p) => p.id !== "plan-free");

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <section className="max-w-7xl mx-auto px-6 py-16 flex-1">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">Pricing</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-[#111827] mt-2">
            Free for emergencies. Premium for the whole library.
          </h1>
          <p className="text-slate-600 mt-4">
            Every verified SA clinician gets permanent free access to our 50 most common emergency protocols. Upgrade any time for the full experience.
          </p>
        </div>

        {/* Free tier explainer */}
        {freePlan && (
          <Card className="mt-10 border-emerald-200 bg-emerald-50/50" data-testid="free-tier-card">
            <CardContent className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <Badge className="bg-emerald-600 text-white border-0">Always free</Badge>
                <h3 className="font-heading font-bold text-xl text-[#111827] mt-2">{freePlan.name}</h3>
                <p className="text-sm text-slate-700 mt-1">
                  50 most common emergency protocols. HPCSA/MP number required. No credit card. No expiry.
                </p>
              </div>
              <Button
                onClick={() => user ? nav("/app/dashboard") : nav("/signup")}
                variant="outline"
                className="border-emerald-700 text-emerald-800 hover:bg-emerald-100 h-11 px-5"
                data-testid="free-cta"
              >
                {user ? "Open dashboard" : "Sign up free"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Trial offer */}
        {user && !user.subscription_expires_at && user.subscription_status === "free" && (
          <div className="mt-5 p-4 rounded-lg bg-[#0B5FFF] text-white flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="font-heading font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4" /> Try Premium free for 7 days</div>
              <div className="text-xs text-white/80 mt-1">No card required. Full access to all 500+ protocols, drugs, calculators, ICD-10 search and more.</div>
            </div>
            <Button onClick={startTrial} disabled={trialLoading} className="bg-white text-[#111827] hover:bg-slate-100" data-testid="start-trial-btn">
              {trialLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</> : "Start free trial"}
            </Button>
          </div>
        )}

        {/* Paid plans */}
        <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {paid.map((plan) => {
            const dark = plan.popular;
            return (
            <Card
              key={plan.id}
              className={`relative overflow-hidden rounded-2xl transition-all duration-200 ${
                dark
                  ? "bg-[#0B5FFF] text-white border border-brand/40 shadow-glow-brand md:scale-[1.04] z-10"
                  : "bg-white border border-border shadow-soft hover:shadow-premium"
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {dark && <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-brand-light via-brand to-brand-deep" />}
              {plan.popular && (
                <Badge className="absolute top-4 left-1/2 -translate-x-1/2 bg-white text-[#0B5FFF] border-0 font-semibold shadow-soft">
                  Recommended
                </Badge>
              )}
              {plan.save_percent > 0 && (
                <Badge className="absolute top-4 right-4 bg-emerald-500 text-white border-0" data-testid={`save-badge-${plan.id}`}>
                  Save {plan.save_percent}%
                </Badge>
              )}
              <CardContent className={dark ? "p-8 pt-12" : "p-6"}>
                <h3 className={`font-heading font-bold text-xl ${dark ? "text-white" : "text-[#111827]"}`}>{plan.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className={`font-heading text-5xl font-bold ${dark ? "text-white" : "text-[#111827]"}`}>
                    R{plan.price_zar.toLocaleString("en-ZA")}
                  </span>
                  <span className={`text-sm ${dark ? "text-white/60" : "text-slate-500"}`}>/ {plan.interval}</span>
                </div>
                {plan.interval === "year" && (
                  <p className={`text-xs font-medium mt-1 ${dark ? "text-brand/90" : "text-emerald-700"}`} data-testid="annual-savings-note">
                    Just R{Math.round(plan.price_zar / 12)}/mo billed once — save R{(89 * 12 - plan.price_zar).toLocaleString("en-ZA")} vs monthly.
                  </p>
                )}
                <p className={`text-sm mt-4 ${dark ? "text-white/75" : "text-slate-600"}`}>
                  Full unlimited access for the period. No auto-billing — renew when it suits you.
                </p>
                <Button
                  onClick={() => subscribe(plan)}
                  disabled={loading === plan.id}
                  className={`w-full mt-6 rounded-full h-11 font-semibold ${
                    dark
                      ? "bg-white hover:bg-slate-100 text-[#0B5FFF]"
                      : "bg-[#0B5FFF] hover:bg-[#0A54E0] text-white"
                  }`}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {loading === plan.id ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Payfast...</>
                  ) : "Subscribe"}
                </Button>
                <p className={`text-[11px] text-center mt-2 ${dark ? "text-white/50" : "text-slate-400"}`} data-testid={`non-refundable-${plan.id}`}>
                  Once-off payment · non-refundable
                </p>
              </CardContent>
            </Card>
            );
          })}
        </div>

        <div className="mt-14 max-w-3xl mx-auto text-center text-sm text-slate-600 space-y-3">
          <p className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-slate-700" data-testid="no-auto-charge-note">
            <strong>You will never be charged automatically.</strong> Your 7-day free trial simply ends and your account moves to the free tier — you can upgrade whenever you're ready. Payment only ever happens when you actively choose a plan.
          </p>
          <p className="text-slate-500">
            Payments processed securely by Payfast in ZAR. Once-off payment — no recurring billing; your access simply expires at the end of the period and you can renew anytime. All payments are final and non-refundable.
            <br />Questions? Email <a href="mailto:rapidcaresasupport@gmail.com" className="text-[#111827] font-medium">rapidcaresasupport@gmail.com</a>.
          </p>
        </div>
      </section>
      <SiteFooter />

      {/* Hidden Payfast form, auto-submitted when fields arrive */}
      {payfast && (
        <form
          ref={formRef}
          action={payfast.url}
          method="post"
          className="hidden"
          data-testid="payfast-form"
        >
          {Object.entries(payfast.fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>
      )}
    </div>
  );
}
